import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Home, FileText, Send, CheckCircle, Clock, AlertTriangle, Plus, RefreshCw, Trash2, RotateCcw, Download, Loader2, Edit, PlayCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RentInvoicesPage = () => {
  const [tenants, setTenants] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [generatingAll, setGeneratingAll] = useState(false);
  const [generatingFor, setGeneratingFor] = useState(null); // Tracks which tenant is being processed
  const [statistics, setStatistics] = useState(null);
  
  // Draft editing state
  const [editDraftDialog, setEditDraftDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editDiscount, setEditDiscount] = useState('');
  const [editDiscountReason, setEditDiscountReason] = useState('');
  const [releasingInvoice, setReleasingInvoice] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [tenantsRes, invoicesRes, statsRes] = await Promise.all([
        axios.get(`${API}/rent/tenants`, { headers }),
        axios.get(`${API}/rent/invoices`, { headers }),
        axios.get(`${API}/rent/statistics`, { headers })
      ]);
      
      setTenants(tenantsRes.data);
      setInvoices(invoicesRes.data);
      setStatistics(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const generateSingleInvoice = async (tenantId) => {
    setGeneratingFor(tenantId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/rent/generate-invoice/${tenantId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      await fetchData(); // Refresh data to show new invoice
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei Rechnungserstellung');
    } finally {
      setGeneratingFor(null);
    }
  };

  const generateAllInvoices = async () => {
    setGeneratingAll(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/rent/generate-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      if (result.created > 0) {
        toast.success(`${result.created} Miet-Rechnungen erstellt`);
      } else {
        toast.info('Keine fälligen Miet-Rechnungen gefunden');
      }
      
      if (result.errors?.length > 0) {
        toast.error(`${result.errors.length} Fehler aufgetreten`);
      }
      
      fetchData();
    } catch (error) {
      toast.error('Fehler bei automatischer Rechnungserstellung');
    } finally {
      setGeneratingAll(false);
    }
  };

  const markAsPaid = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/rent/invoices/${invoiceId}/mark-paid`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rechnung als bezahlt markiert');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Markieren als bezahlt');
    }
  };

  const resendInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/rent/invoices/${invoiceId}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Rechnung erneut versendet');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Versenden');
    }
  };

  const refundInvoice = async (invoiceId, invoiceNumber) => {
    if (!window.confirm(`Miet-Rechnung ${invoiceNumber} wirklich rückerstatten?`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/rent/invoices/${invoiceId}/refund`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Rechnung rückerstattet');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Rückerstatten');
    }
  };

  const deleteInvoice = async (invoiceId, invoiceNumber) => {
    if (!window.confirm(`⚠️ Miet-Rechnung ${invoiceNumber} wirklich LÖSCHEN?\n\nDiese Aktion kann nicht rückgängig gemacht werden!`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API}/rent/invoices/${invoiceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Rechnung gelöscht');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  // Open draft edit dialog
  const openEditDraftDialog = (invoice) => {
    setEditingInvoice(invoice);
    setEditAmount(invoice.amount?.toString() || '');
    setEditDiscount(invoice.discount_amount?.toString() || '0');
    setEditDiscountReason(invoice.discount_reason || '');
    setEditDraftDialog(true);
  };

  // Save draft changes
  const saveDraftChanges = async () => {
    if (!editingInvoice) return;
    
    try {
      const token = localStorage.getItem('token');
      const amount = parseFloat(editAmount);
      const discountAmount = parseFloat(editDiscount) || 0;
      
      if (isNaN(amount) || amount <= 0) {
        toast.error('Bitte gültigen Betrag eingeben');
        return;
      }
      
      if (discountAmount < 0) {
        toast.error('Rabatt kann nicht negativ sein');
        return;
      }
      
      if (discountAmount > amount) {
        toast.error('Rabatt kann nicht grösser als der Betrag sein');
        return;
      }
      
      const params = { 
        amount,
        discount_amount: discountAmount
      };
      
      if (editDiscountReason) {
        params.discount_reason = editDiscountReason;
      }
      
      await axios.put(
        `${API}/rent/invoices/${editingInvoice.id}`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params
        }
      );
      
      toast.success('Entwurf aktualisiert');
      setEditDraftDialog(false);
      setEditingInvoice(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  // Release draft invoice
  const releaseDraftInvoice = async (invoice, newAmount = null, newDiscount = null, newDiscountReason = null) => {
    setReleasingInvoice(invoice.id);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      
      if (newAmount !== null) {
        params.amount = newAmount;
      }
      
      if (newDiscount !== null) {
        params.discount_amount = newDiscount;
      }
      
      if (newDiscountReason) {
        params.discount_reason = newDiscountReason;
      }
      
      const response = await axios.put(
        `${API}/rent/invoices/${invoice.id}/release`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params
        }
      );
      
      toast.success(response.data.message || 'Rechnung freigegeben und versendet');
      setEditDraftDialog(false);
      setEditingInvoice(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Freigeben');
    } finally {
      setReleasingInvoice(null);
    }
  };

  const downloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/rent/invoices/${invoiceId}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Mietrechnung_${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF heruntergeladen');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Fehler beim Herunterladen der PDF');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-orange-100 text-orange-800 border-orange-300', label: 'Entwurf', icon: Edit },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Ausstehend', icon: Clock },
      sent: { color: 'bg-blue-100 text-blue-800', label: 'Versendet', icon: Send },
      paid: { color: 'bg-green-100 text-green-800', label: 'Bezahlt', icon: CheckCircle },
      overdue: { color: 'bg-red-100 text-red-800', label: 'Überfällig', icon: AlertTriangle },
      refunded: { color: 'bg-purple-100 text-purple-800', label: 'Rückerstattet', icon: AlertTriangle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon size={12} />
        {config.label}
      </Badge>
    );
  };

  const filteredInvoices = invoices.filter(inv => {
    if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
    if (selectedTenant && inv.tenant_user_id !== selectedTenant) return false;
    return true;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH');
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <RefreshCw className="animate-spin mr-2" /> Laden...
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Home className="h-6 w-6 text-green-600" />
            Miet-Rechnungen
          </h1>
          <p className="text-gray-600 mt-1">Verwalten Sie Mietabrechnungen für Ihre Räume</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchData}
          >
            <RefreshCw size={16} className="mr-2" />
            Aktualisieren
          </Button>
          <Button
            onClick={generateAllInvoices}
            disabled={generatingAll}
            className="bg-green-600 hover:bg-green-700"
          >
            {generatingAll ? (
              <>
                <RefreshCw size={16} className="mr-2 animate-spin" />
                Generiere...
              </>
            ) : (
              <>
                <Plus size={16} className="mr-2" />
                Alle fälligen erstellen
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Gesamt {statistics.year}</p>
                  <p className="text-2xl font-bold">{statistics.total_invoices}</p>
                </div>
                <FileText className="h-10 w-10 text-gray-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Bezahlt</p>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(statistics.total_paid)}</p>
                </div>
                <CheckCircle className="h-10 w-10 text-green-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600">Offen</p>
                  <p className="text-2xl font-bold text-yellow-700">{formatCurrency(statistics.total_pending)}</p>
                </div>
                <Clock className="h-10 w-10 text-yellow-300" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tenants Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Mieter</CardTitle>
          <CardDescription>Aktive Mieter mit Mieteinstellungen</CardDescription>
        </CardHeader>
        <CardContent>
          {tenants.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              Keine Mieter gefunden. Erstellen Sie unter "Personal" einen Benutzer mit der Rolle "Mieter".
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tenants.map(tenant => (
                <div
                  key={tenant.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-semibold">{tenant.first_name} {tenant.last_name}</h4>
                      <p className="text-sm text-gray-500">{tenant.email}</p>
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700">
                      Mieter
                    </Badge>
                  </div>
                  
                  {tenant.rent_amount ? (
                    <div className="space-y-1 text-sm mb-3">
                      <p><strong>Miete:</strong> {formatCurrency(tenant.rent_amount)}</p>
                      <p><strong>Intervall:</strong> {tenant.rent_payment_interval === 'bimonthly' ? 'Alle 2 Monate' : 'Monatlich'}</p>
                      {tenant.rented_room && <p><strong>Raum:</strong> {tenant.rented_room}</p>}
                      {tenant.rent_first_billing_month && (
                        <p><strong>Erster Abrechnungsmonat:</strong> {new Date(tenant.rent_first_billing_month + '-01').toLocaleDateString('de-CH', { month: 'long', year: 'numeric' })}</p>
                      )}
                      <p>
                        <strong>Modus:</strong>{' '}
                        {tenant.rent_auto_invoice === false ? (
                          <span className="text-orange-600">Manuell (variable Miete)</span>
                        ) : (
                          <span className="text-green-600">Automatisch</span>
                        )}
                      </p>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { window.location.href = `/staff?editUser=${tenant.id}`; }}
                      className="text-yellow-700 hover:text-yellow-800 underline text-sm mb-3 block text-left"
                      data-testid={`set-rent-data-link-${tenant.id}`}
                    >
                      ⚠️ Keine Mietdaten hinterlegt — jetzt in „Personal" hinterlegen →
                    </button>
                  )}
                  
                  {tenant.rent_amount ? (
                    <Button
                      size="sm"
                      onClick={() => generateSingleInvoice(tenant.id)}
                      disabled={generatingFor === tenant.id}
                      className="w-full"
                      data-testid={`create-invoice-btn-${tenant.id}`}
                    >
                      {generatingFor === tenant.id ? (
                        <>
                          <Loader2 size={14} className="mr-2 animate-spin" />
                          Erstelle...
                        </>
                      ) : (
                        <>
                          <FileText size={14} className="mr-2" />
                          Rechnung erstellen
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { window.location.href = `/staff?editUser=${tenant.id}`; }}
                      className="w-full"
                      data-testid={`set-rent-data-btn-${tenant.id}`}
                    >
                      <FileText size={14} className="mr-2" />
                      Mietdaten hinterlegen
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle className="text-lg">Rechnungen</CardTitle>
              <CardDescription>Alle erstellten Miet-Rechnungen</CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="draft">Entwürfe</SelectItem>
                  <SelectItem value="pending">Ausstehend</SelectItem>
                  <SelectItem value="sent">Versendet</SelectItem>
                  <SelectItem value="paid">Bezahlt</SelectItem>
                  <SelectItem value="overdue">Überfällig</SelectItem>
                  <SelectItem value="refunded">Rückerstattet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredInvoices.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              Keine Rechnungen gefunden
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rechnungs-Nr.</TableHead>
                    <TableHead>Mieter</TableHead>
                    <TableHead>Zeitraum</TableHead>
                    <TableHead>Betrag</TableHead>
                    <TableHead>Fällig</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map(invoice => (
                    <TableRow 
                      key={invoice.id}
                      className={invoice.status === 'draft' ? 'bg-orange-50 hover:bg-orange-100' : ''}
                    >
                      <TableCell className="font-mono text-sm">
                        {invoice.invoice_number}
                        {invoice.status === 'draft' && (
                          <Badge className="ml-2 bg-orange-100 text-orange-700 text-xs">
                            Entwurf
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{invoice.tenant_name}</p>
                          <p className="text-xs text-gray-500">{invoice.rented_room}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {invoice.period_display}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-semibold">{formatCurrency(invoice.final_amount || invoice.amount)}</p>
                          {invoice.discount_amount > 0 && (
                            <div className="text-xs">
                              <span className="text-gray-500 line-through">{formatCurrency(invoice.amount)}</span>
                              <span className="text-green-600 ml-1">-{formatCurrency(invoice.discount_amount)}</span>
                              {invoice.discount_reason && (
                                <p className="text-green-600 italic">({invoice.discount_reason})</p>
                              )}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(invoice.due_date)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(invoice.status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2 flex-wrap">
                          {/* Draft-spezifische Aktionen */}
                          {invoice.status === 'draft' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditDraftDialog(invoice)}
                                className="text-orange-600 hover:bg-orange-50"
                                title="Betrag anpassen"
                                data-testid={`edit-draft-btn-${invoice.id}`}
                              >
                                <Edit size={14} className="mr-1" />
                                Bearbeiten
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => releaseDraftInvoice(invoice)}
                                disabled={releasingInvoice === invoice.id}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                title="Freigeben und versenden"
                                data-testid={`release-draft-btn-${invoice.id}`}
                              >
                                {releasingInvoice === invoice.id ? (
                                  <Loader2 size={14} className="mr-1 animate-spin" />
                                ) : (
                                  <PlayCircle size={14} className="mr-1" />
                                )}
                                Freigeben
                              </Button>
                            </>
                          )}
                          {/* Normale Aktionen für nicht-Entwürfe */}
                          {invoice.status !== 'paid' && invoice.status !== 'refunded' && invoice.status !== 'draft' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => resendInvoice(invoice.id)}
                                className="text-blue-600 hover:bg-blue-50"
                                title="Erneut versenden"
                              >
                                <Send size={14} className="mr-1" />
                                Senden
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => markAsPaid(invoice.id)}
                                className="text-green-600 hover:bg-green-50"
                              >
                                <CheckCircle size={14} className="mr-1" />
                                Bezahlt
                              </Button>
                            </>
                          )}
                          {/* Rückerstatten - nur für bezahlte Rechnungen */}
                          {invoice.status === 'paid' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => refundInvoice(invoice.id, invoice.invoice_number)}
                              className="text-purple-600 hover:bg-purple-50"
                              title="Rückerstatten"
                            >
                              <RotateCcw size={14} className="mr-1" />
                              Rückerstatten
                            </Button>
                          )}
                          {/* PDF Download - immer möglich */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadPdf(invoice.id, invoice.invoice_number)}
                            className="text-gray-600 hover:bg-gray-50"
                            title="PDF herunterladen"
                          >
                            <Download size={14} />
                          </Button>
                          {/* Löschen - immer möglich */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteInvoice(invoice.id, invoice.invoice_number)}
                            className="text-red-600 hover:bg-red-50"
                            title="Löschen"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Draft Dialog */}
      <Dialog open={editDraftDialog} onOpenChange={setEditDraftDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-orange-600" />
              Entwurf bearbeiten
            </DialogTitle>
            <DialogDescription>
              Passen Sie den Mietbetrag an, bevor Sie die Rechnung freigeben.
            </DialogDescription>
          </DialogHeader>
          
          {editingInvoice && (
            <div className="space-y-4 py-2">
              {/* Mieter-Info Box */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-gray-500">Mieter:</span> <strong>{editingInvoice.tenant_name}</strong></p>
                <p><span className="text-gray-500">Zeitraum:</span> {editingInvoice.period_display}</p>
                <p><span className="text-gray-500">Raum:</span> {editingInvoice.rented_room}</p>
                <p><span className="text-gray-500">Rechnungs-Nr.:</span> {editingInvoice.invoice_number}</p>
              </div>
              
              {/* Mietbetrag */}
              <div>
                <Label htmlFor="edit-amount" className="text-sm font-medium">Mietbetrag (CHF)</Label>
                <Input
                  id="edit-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  placeholder="z.B. 1500.00"
                  className="mt-1 border-gray-200 shadow-none focus:ring-0 focus:border-gray-300"
                  data-testid="edit-draft-amount-input"
                />
              </div>
              
              {/* Rabatt-Sektion */}
              <div className="pt-3 border-t border-gray-200">
                <p className="text-sm font-medium text-green-700 mb-2">💸 Rabatt (optional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="edit-discount" className="text-xs text-gray-600">Rabattbetrag (CHF)</Label>
                    <Input
                      id="edit-discount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editDiscount}
                      onChange={(e) => setEditDiscount(e.target.value)}
                      placeholder="0.00"
                      className="mt-1 border-gray-200 shadow-none focus:ring-0 focus:border-gray-300"
                      data-testid="edit-draft-discount-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-discount-reason" className="text-xs text-gray-600">Rabattgrund</Label>
                    <Input
                      id="edit-discount-reason"
                      type="text"
                      value={editDiscountReason}
                      onChange={(e) => setEditDiscountReason(e.target.value)}
                      placeholder="z.B. Treuebonus"
                      className="mt-1 border-gray-200 shadow-none focus:ring-0 focus:border-gray-300"
                      data-testid="edit-draft-discount-reason-input"
                    />
                  </div>
                </div>
              </div>
              
              {/* Zusammenfassung */}
              <div className="bg-gray-50 rounded-lg p-3 mt-2">
                <p className="text-xs text-gray-500 mb-2">Zusammenfassung</p>
                <div className="flex justify-between text-sm">
                  <span>Mietbetrag:</span>
                  <span>{formatCurrency(parseFloat(editAmount) || 0)}</span>
                </div>
                {parseFloat(editDiscount) > 0 && (
                  <div className="flex justify-between text-sm text-green-600 mt-1">
                    <span>Rabatt{editDiscountReason ? ` (${editDiscountReason})` : ''}:</span>
                    <span>-{formatCurrency(parseFloat(editDiscount) || 0)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center border-t border-gray-200 mt-2 pt-2">
                  <span className="font-semibold">Endbetrag:</span>
                  <span className="text-xl font-bold text-blue-600">
                    {formatCurrency((parseFloat(editAmount) || 0) - (parseFloat(editDiscount) || 0))}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setEditDraftDialog(false);
                setEditingInvoice(null);
              }}
            >
              Abbrechen
            </Button>
            <Button
              variant="outline"
              onClick={saveDraftChanges}
              className="text-orange-600 hover:bg-orange-50"
            >
              <Edit size={14} className="mr-1" />
              Speichern
            </Button>
            <Button
              onClick={() => {
                const amount = parseFloat(editAmount);
                const discount = parseFloat(editDiscount) || 0;
                if (!isNaN(amount) && amount > 0) {
                  releaseDraftInvoice(editingInvoice, amount, discount, editDiscountReason);
                } else {
                  releaseDraftInvoice(editingInvoice);
                }
              }}
              disabled={releasingInvoice === editingInvoice?.id}
              className="bg-green-600 hover:bg-green-700"
              data-testid="release-draft-confirm-btn"
            >
              {releasingInvoice === editingInvoice?.id ? (
                <Loader2 size={14} className="mr-1 animate-spin" />
              ) : (
                <PlayCircle size={14} className="mr-1" />
              )}
              Freigeben & Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RentInvoicesPage;
